/*
    js/language tools/helpers
*/
import { integerRandom, integerTea, RandomFunction } from './mathTools';

// object operations
//-----------------------------------------------------------------------------
export function objectCreate<K extends string, V>(
  keys: readonly K[],
  initializer: (key: K) => V,
): Record<K, V> {
  const object: { [key in K]?: V } = {};
  for (const key of keys) {
    object[key] = initializer(key);
  }
  return object as Record<K, V>;
}

export function objectAccess<T>(
  key: string,
  object: any,
  init: T = {} as T,
): any {
  return object[key] || (object[key] = init);
}

// array operations
//-----------------------------------------------------------------------------
/*
    create array
*/
export function arrayCreate<T>(
  count: number,
  builder: (index: number) => T,
): T[] {
  const array = new Array<T>(count);
  for (let i = 0; i < count; ++i) {
    array[i] = builder(i);
  }
  return array;
}

/*
    reverse iterate an array. this is also useful for safe mid iteration removal
    of the current item.
*/
export function arrayReverseIterate<T>(
  array: T[],
  listener: (value: T, index: number) => void,
) {
  for (let i = array.length; i--;) {
    listener(array[i], i);
  }
}

/*
    remove item by value form array
*/
export function arrayRemove<T>(array: T[], value: T): T[] {
  const index = array.indexOf(value);
  if (index >= 0) {
    array.splice(index, 1);
  }
  return array;
}

/*
    remove multiple indexes in an array
*/
export function arrayRemoveMany<T>(array: T[], indexes: number[]): T[] {
  arrayReverseIterate(array, (_, i) => {
    if (indexes.includes(i)) array.splice(i, 1);
  });
  return array;
}

/*
    swap 2 elements in an array
*/
export function arraySwap<T>(array: T[], i0: number, i1: number) {
  const v0 = array[i0];
  array[i0] = array[i1];
  array[i1] = v0;
}

/*
    public get a random entry from an array
*/
export function arrayRandom<T>(
  array: readonly T[],
  fRandom: RandomFunction = Math.random,
): T | undefined {
  if (array.length === 0) return undefined;
  return array[integerRandom(0, array.length - 1, fRandom)];
}

/*
    shuffle an array
*/
export function arrayShuffle<T>(
  array: T[],
  fRandom: RandomFunction = Math.random,
): T[] {
  for (let i = 0; i < array.length; ++i) {
    arraySwap(array, i, integerRandom(0, array.length - 1, fRandom));
  }
  return array;
}

/*
    public get a tea hashed entry from an array
*/
export function arrayTea<T>(array: T[], v0: string | number, v1?: number): T {
  return array[integerTea(0, array.length - 1, v0, v1)];
}

// 2d array operations
//-----------------------------------------------------------------------------
/*
    create 2d array
*/
export function arrayCreate2<T>(
  columns: number,
  rows: number,
  builder: (column: number, row: number) => T,
): T[][] {
  const array = new Array<T[]>(columns);

  for (let column = 0; column < columns; ++column) {
    const cells = (array[column] = new Array<T>(rows));

    for (let row = 0; row < rows; ++row) {
      cells[row] = builder(column, row);
    }
  }

  return array;
}

/*
    iterate 2d array
*/
export function arrayIterate2<T>(
  array: T[][],
  listener: (value: T, column: number, row: number) => void,
) {
  for (let column = 0; column < array.length; ++column) {
    const cells = array[column];
    for (let row = 0; row < cells.length; ++row) {
      listener(cells[row], column, row);
    }
  }
}

// string operations
//-----------------------------------------------------------------------------
export function stringFormat(text: string, args: any[]): string {
  for (let i = 0; i < args.length; ++i) {
    text = text.replace(/{(\d+)}/g, (match, number) => args[number]);
  }

  return text;
}

// window operations
//-----------------------------------------------------------------------------
export function windowReload() {
  // dont use window.location.reload(), its broken for some(?) iphones
  window.location.href = window.location.href; // eslint-disable-line no-self-assign
}
